import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subject, combineLatest, takeUntil } from 'rxjs';

import { AuthService } from '../../../services/auth.service';
import { LoaderService } from '../../../services/loader.service';
import { BasicDialogComponent } from '../../../../shared/components/basic-dialog/basic-dialog.component';
import { DefaultImageDirective } from '../../../../shared/directives/default-image.directive';
import type { IUser, SystemRole } from '../../../../shared/models/user.model';
import type { RoleWithMarket } from '../../../../shared/models/role.model';

@Component({
  selector: 'app-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    MatMenuModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    DefaultImageDirective
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnDestroy {
  @Input() userName = '';
  @Output() readonly roleChanged = new EventEmitter<string>();

  // Services
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly destroy$ = new Subject<void>();

  // State
  currentUser: IUser | null = null;
  rolesWithMarket: RoleWithMarket[] = [];
  selectedRoleIndex: number | null = null;

  // Computed properties
  get showRolesDropdown(): boolean {
    return this.rolesWithMarket.length > 1;
  }

  get profileImageUrl(): string {
    const prid = this.currentUser?.prid || '';
    return prid ? `https://adhoc.rd.astrazeneca.net/api/thumbs/${prid}?appid=befair` : '';
  }

  get selectedRole(): RoleWithMarket | undefined {
    return this.selectedRoleIndex != null ? this.rolesWithMarket[this.selectedRoleIndex] : undefined;
  }

  get singleRoleIcon(): string {
    return this.getRoleIcon(this.currentUser?.system_role_info?.[0]?.role_name);
  }

  get userInitial(): string {
    return this.userName.charAt(0).toUpperCase();
  }

  constructor() {
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRoleIcon(roleName?: string): string {
    const role = roleName?.toLowerCase() || '';

    if (role.includes('system admin')) return 'admin_panel_settings';
    if (role.includes('manager')) return 'person';
    if (role.includes('market admin')) return 'store';

    return 'badge';
  }

  openDialog(): void {
    this.dialog.open(BasicDialogComponent);
  }

  switchRole(index: number): void {
    const roleInfo = this.currentUser?.system_role_info?.[index];
    if (!roleInfo) return;

    this.selectedRoleIndex = index;
    this.loaderService.show('Switching role...');

    this.authService.setSelectedRoleContext(roleInfo);
    this.roleChanged.emit(roleInfo.role_name);

    this.cdr.markForCheck();

    setTimeout(() => {
      this.loaderService.hide();
      this.router.navigate(['/dashboard']).catch(() => {
        // Navigation failed - user may already be on dashboard
      });
    }, 500);
  }

  private initializeSubscriptions(): void {
    this.subscribeToUserName();
    this.subscribeToUserDataAndRole();
  }

  private subscribeToUserName(): void {
    this.authService.userName
      .pipe(takeUntil(this.destroy$))
      .subscribe(name => {
        this.userName = name;
        this.cdr.markForCheck();
      });
  }

  private subscribeToUserDataAndRole(): void {
    combineLatest([
      this.authService.userData$,
      this.authService.selectedRoleContext$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, selectedRole]) => {
        this.currentUser = user;

        if (!user?.system_role_info?.length) {
          this.clearRoles();
          this.cdr.markForCheck();
          return;
        }

        const isInitialLoad = this.rolesWithMarket.length === 0;
        this.rolesWithMarket = this.buildRolesWithMarket(user.system_role_info);

        // Sync selected role index
        if (selectedRole) {
          this.syncSelectedRoleIndex(user.system_role_info, selectedRole);
        } else if (isInitialLoad) {
          this.setDefaultRole();
        }

        this.cdr.markForCheck();
      });
  }

  private buildRolesWithMarket(roles: IUser['system_role_info']): RoleWithMarket[] {
    return roles.map((role, index) => {
      const roleWithId = role as SystemRole & { role_id?: number };
      return {
        role_name: role.role_name || '',
        market: role.market_name || null,
        market_id: role.market_id,
        role_id: roleWithId.role_id || role.id,
        role_code: role.role_code,
        showMarket: role.role_name?.toLowerCase() !== 'system admin',
        uniqueKey: String(index),
        index,
        icon: this.getRoleIcon(role.role_name)
      };
    });
  }

  private syncSelectedRoleIndex(roles: SystemRole[], selectedRole: SystemRole): void {
    const matchingIndex = roles.findIndex(role =>
      this.isSameRole(role, selectedRole)
    );

    if (matchingIndex >= 0) {
      this.selectedRoleIndex = matchingIndex;
    } else {
      // Selected role no longer exists, clear and set default
      this.authService.setSelectedRoleContext(null);
      this.setDefaultRole();
    }
  }

private isSameRole(role1: SystemRole, role2: SystemRole): boolean {
    // Get role_id from either role_id or id field (API vs interface mismatch)
    const role1WithRoleId = role1 as SystemRole & { role_id?: number };
    const role2WithRoleId = role2 as SystemRole & { role_id?: number };
    const role1Id = role1WithRoleId.role_id || role1.id;
    const role2Id = role2WithRoleId.role_id || role2.id;

    // Compare role_id, market_id, and business_unit_id
    // Also compare assigned_at to distinguish between duplicate role assignments
    return role1Id === role2Id &&
           role1.market_id === role2.market_id &&
           role1.business_unit_id === role2.business_unit_id &&
           role1.assigned_at === role2.assigned_at;
  }

  private setDefaultRole(): void {
    if (this.currentUser?.system_role_info?.length) {
      this.selectedRoleIndex = 0;
      this.authService.setSelectedRoleContext(this.currentUser.system_role_info[0]);
    }
  }

  private clearRoles(): void {
    this.rolesWithMarket = [];
    this.selectedRoleIndex = null;
  }
}
